/**
 * CareflowDrawer — Add/Edit a careflow.
 *
 * Shows name, description, and entity selectors depending on careflow type:
 *   Default (1): no entities
 *   Device (3): device selector
 *   Resident (4): resident group selector
 *   Area (5): area group selector
 *   Event (6): event category + event type dropdowns
 *
 * Props:
 *   open            — drawer visibility
 *   onClose         — close callback
 *   onSave          — save callback (formData, oldPayload?)
 *   onDelete        — delete callback (for edit mode)
 *   saving          — boolean, disables buttons while saving
 *   editTarget      — null for add, careflow object for edit
 *   careflowTypeId  — 1, 3, 4, 5, or 6
 *   eventCategories — array of event category objects
 *   eventTypes      — array of event type objects
 *   areaGroups      — array of area group records
 *   residentGroups  — array of resident group records
 *   devices         — array of device records
 */

import { useState, useEffect, useMemo } from 'react';
import EntitySelectorTable from './EntitySelectorTable.jsx';
import { sortFunc } from '../../../../libs.jsx';

const TYPE_LABELS = { 1: 'Default', 3: 'Device', 4: 'Resident', 5: 'Area', 6: 'Event' };

const AG_COLS = [
    { key: 'area_group_name', label: 'Group', align: 'left' },
    { key: 'area_group_description', label: 'Description', align: 'left' },
];
const RG_COLS = [
    { key: 'resident_group_name', label: 'Group', align: 'left' },
    { key: 'resident_description', label: 'Description', align: 'left' },
];
const DEV_COLS = [
    { key: 'device_name', label: 'Name', align: 'left' },
    { key: 'descriptions', label: 'Description', align: 'left' },
    { key: 'manufacture_uuid', label: 'TX ID', align: 'center' },
];

const CareflowDrawer = ({
    open,
    onClose,
    onSave,
    onDelete,
    saving,
    editTarget,
    careflowTypeId,
    eventCategories = [],
    eventTypes = [],
    areaGroups = [],
    residentGroups = [],
    devices = [],
}) => {
    const isEdit = !!editTarget;
    const label = TYPE_LABELS[careflowTypeId] || 'Default';

    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [eventCatId, setEventCatId] = useState('');
    const [eventTypeId, setEventTypeId] = useState('');
    const [entityIds, setEntityIds] = useState([]);
    const [errors, setErrors] = useState({});
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // Filter out event_category_id === 1 (system)
    const filteredCats = useMemo(() =>
        (eventCategories || [])
            .filter((c) => c.event_category_id !== 1)
            .sort((a, b) => sortFunc(a, b, 'event_category_name', 'asc')),
        [eventCategories]
    );

    // Event types filtered by selected category
    const filteredTypes = useMemo(() =>
        (eventTypes || [])
            .filter((t) => t.event_category_id === parseInt(eventCatId))
            .sort((a, b) => sortFunc(a, b, 'event_type_name', 'asc')),
        [eventTypes, eventCatId]
    );

    // Reset form when drawer opens
    useEffect(() => {
        if (!open) return;

        if (editTarget) {
            setName(editTarget.careflow_name || '');
            setDesc(editTarget.careflow_description || '');
            setEventCatId(editTarget.event_category_id ? String(editTarget.event_category_id) : '');
            setEventTypeId(editTarget.event_type_id ? String(editTarget.event_type_id) : '');

            // Load entity IDs
            const ids = (editTarget.entities || []).map((e) => String(e.reference_id));
            setEntityIds(ids);
        } else {
            setName('');
            setDesc('');
            setEventCatId(filteredCats.length > 0 ? String(filteredCats[0].event_category_id) : '');
            setEventTypeId('');
            setEntityIds([]);
        }
        setErrors({});
        setConfirmingDelete(false);
    }, [open, editTarget]);

    const namePattern = /^[a-zA-Z0-9_ \-/]+$/;

    const validate = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Name is required.';
        else if (!namePattern.test(name.trim())) errs.name = 'Only letters, numbers, spaces, and - _ / are allowed.';
        if (!desc.trim()) errs.desc = 'Description is required.';
        if (careflowTypeId === 6 && !eventCatId) errs.eventCat = 'Category is required.';
        if (careflowTypeId === 6 && !eventTypeId) errs.eventType = 'Event type is required.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Live form validity for button state
    const isFormValid = useMemo(() => {
        const n = name.trim();
        if (!n || !namePattern.test(n)) return false;
        if (!desc.trim()) return false;
        if (careflowTypeId === 6 && (!eventCatId || !eventTypeId)) return false;
        return true;
    }, [name, desc, careflowTypeId, eventCatId, eventTypeId]);

    const clearError = (field) => {
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleSave = () => {
        if (!validate()) return;

        const eCatId = careflowTypeId === 6 ? parseInt(eventCatId) : 1;
        const eTypeId = careflowTypeId === 6 ? parseInt(eventTypeId) : 1;

        onSave({
            careflow_name: name.trim(),
            careflow_description: desc.trim(),
            careflow_type_id: careflowTypeId,
            event_category_id: eCatId,
            event_type_id: eTypeId,
            attached_entities: entityIds,
            window_frequency_id: editTarget?.window_frequency_id || 1,
        });
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">
                        {isEdit ? `Edit ${label} Careflow` : `Add ${label} Careflow`}
                    </h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {/* Name */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Name <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.name ? ' is-invalid' : ''}`}
                            type="text"
                            value={name}
                            placeholder="Enter careflow name"
                            onChange={(e) => { setName(e.target.value); clearError('name'); }}
                        />
                        {errors.name ? <p className="dp-drawer__error">{errors.name}</p> : null}
                    </div>

                    {/* Description */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Description <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.desc ? ' is-invalid' : ''}`}
                            type="text"
                            value={desc}
                            placeholder="Enter description"
                            onChange={(e) => { setDesc(e.target.value); clearError('desc'); }}
                        />
                        {errors.desc ? <p className="dp-drawer__error">{errors.desc}</p> : null}
                    </div>

                    {/* Event selector (type 6 only) */}
                    {careflowTypeId === 6 ? (
                        <>
                            <div className="dp-drawer__field">
                                <label className="dp-drawer__label">
                                    Event Category <span className="dp-drawer__required">*</span>
                                </label>
                                <select
                                    className={`dp-drawer__input${errors.eventCat ? ' is-invalid' : ''}`}
                                    value={eventCatId}
                                    onChange={(e) => { setEventCatId(e.target.value); setEventTypeId(''); clearError('eventCat'); }}
                                >
                                    <option value="" disabled>Select category</option>
                                    {filteredCats.map((cat) => (
                                        <option key={cat.event_category_id} value={cat.event_category_id}>
                                            {cat.event_category_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.eventCat ? <p className="dp-drawer__error">{errors.eventCat}</p> : null}
                            </div>
                            <div className="dp-drawer__field">
                                <label className="dp-drawer__label">
                                    Event Type <span className="dp-drawer__required">*</span>
                                </label>
                                <select
                                    className={`dp-drawer__input${errors.eventType ? ' is-invalid' : ''}`}
                                    value={eventTypeId}
                                    onChange={(e) => { setEventTypeId(e.target.value); clearError('eventType'); }}
                                >
                                    <option value="" disabled>Select event type</option>
                                    {filteredTypes.map((t) => (
                                        <option key={t.event_type_id} value={t.event_type_id}>
                                            {t.event_type_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.eventType ? <p className="dp-drawer__error">{errors.eventType}</p> : null}
                            </div>
                        </>
                    ) : null}

                    {/* Area group selector (type 5) */}
                    {careflowTypeId === 5 ? (
                        <EntitySelectorTable
                            title="Area Groups"
                            data={areaGroups}
                            columns={AG_COLS}
                            selectedIds={entityIds}
                            onSelectionChange={setEntityIds}
                            searchKeys={['area_group_name', 'area_group_description']}
                            idKey="area_group_id"
                        />
                    ) : null}

                    {/* Resident group selector (type 4) */}
                    {careflowTypeId === 4 ? (
                        <EntitySelectorTable
                            title="Resident Groups"
                            data={residentGroups}
                            columns={RG_COLS}
                            selectedIds={entityIds}
                            onSelectionChange={setEntityIds}
                            searchKeys={['resident_group_name', 'resident_description']}
                            idKey="resident_group_id"
                        />
                    ) : null}

                    {/* Device selector (type 3) */}
                    {careflowTypeId === 3 ? (
                        <EntitySelectorTable
                            title="Devices"
                            data={devices}
                            columns={DEV_COLS}
                            selectedIds={entityIds}
                            onSelectionChange={setEntityIds}
                            searchKeys={['device_name', 'descriptions', 'manufacture_uuid']}
                            idKey="device_id"
                        />
                    ) : null}
                </div>

                <div className="dp-drawer__footer" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                    {confirmingDelete ? (
                        <div className="cf-inline-confirm">
                            <p className="cf-inline-confirm__message">
                                Are you sure you want to delete this careflow and all its workflows, escalations, and outputs?
                            </p>
                            <div className="cf-inline-confirm__actions">
                                <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmingDelete(false)} disabled={saving}>
                                    No, Keep It
                                </button>
                                <button className="dp-btn dp-btn--danger" onClick={onDelete} disabled={saving}>
                                    {saving ? 'Deleting...' : 'Yes, Delete'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            {isEdit && onDelete ? (
                                <button className="dp-btn dp-btn--danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
                                    Delete
                                </button>
                            ) : (
                                <span />
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                                <button className="dp-btn dp-btn--primary" disabled={saving || !isFormValid} onClick={handleSave}>
                                    {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Careflow')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CareflowDrawer;
